import { cn } from '@/lib/utils';
import { Users, MapPin, Linkedin, FileText, PenTool } from 'lucide-react';
import { TYPOGRAPHY, SPACING } from '@/design-system';
import { usePlatformStats } from '@/hooks/usePlatformStats';
import { Skeleton } from '@/components/ui/skeleton';
import { LComponents } from '@/components/i18n';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useLocale } from '@/contexts/LocaleContext';
import { PlatformStatItem } from '@/components/shared/PlatformStatItem';

export function SignupStatsPanel() {
  const { data: stats, isLoading } = usePlatformStats();
  const { formatNumber } = useLocale();

  const content = isLoading ? (
    <>
      <Skeleton className="h-6 w-32 mb-1" />
      <Skeleton className={cn(SPACING.marginBottom4, 'h-3 w-40')} />
      <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, i) => (
          <PlatformStatItem
            key={i}
            icon={null}
            value={0}
            label=""
            isLoading
            variant="default"
          />
        ))}
      </div>
    </>
  ) : (
    <>
      <h2 className={cn(TYPOGRAPHY.metricValueSmall, 'text-foreground mb-1')}>
        <LComponents id="platform_stats.stats_title" defaultValue="Statistics Overview" />
      </h2>
      <p className={cn(TYPOGRAPHY.caption, SPACING.sectionHeaderLarge)}>
        <LComponents id="platform_stats.stats_subtitle" defaultValue="Join a global network of tax professionals" />
      </p>
      
      <div className="flex flex-col gap-4">
        <PlatformStatItem
          icon={<Users className="w-4 h-4 text-primary" />}
          value={stats?.total_users}
          label={<LComponents id="platform_stats.total_users" defaultValue="Total Users" />}
          formatNumber={formatNumber}
          variant="default"
        />
        <PlatformStatItem
          icon={<Linkedin className="w-4 h-4 text-primary" />}
          value={stats?.linkedin_followers}
          label={<LComponents id="platform_stats.linkedin_followers" defaultValue="LinkedIn Followers" />}
          formatNumber={formatNumber}
          variant="default"
        />
        <PlatformStatItem
          icon={<FileText className="w-4 h-4 text-primary" />}
          value={(stats?.total_publications || 0) + (stats?.total_comments || 0)}
          label={<LComponents id="platform_stats.insights" defaultValue="Insights" />}
          formatNumber={formatNumber}
          variant="default"
        />
        <PlatformStatItem
          icon={<PenTool className="w-4 h-4 text-primary" />}
          value={stats?.total_authors}
          label={<LComponents id="platform_stats.authors" defaultValue="Authors" />}
          formatNumber={formatNumber}
          variant="default"
        />
        <PlatformStatItem
          icon={<MapPin className="w-4 h-4 text-primary" />}
          value={stats?.total_locations}
          label={<LComponents id="platform_stats.global_locations" defaultValue="Global Locations" />}
          formatNumber={formatNumber}
          variant="default"
        />
      </div>
    </>
  );

  return (
    <div className="h-full flex flex-col justify-between p-5 bg-muted/30 rounded-2xl">
      <div>{content}</div>
      <div className="mt-auto pt-6">
        <LanguageSelector />
      </div>
    </div>
  );
}
